// Modules
import React, { useCallback, useEffect, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'

// Application
import { useRecoverHook } from 'hooks'
import { HeaderProps } from 'typings'
import { PageTemplate } from './styles'
import { RecoverStep } from 'typings/recoverpassword'
import { usePageTracker } from 'hooks/usePageTracker'
import { getQueryParamsValue } from 'utils'
import { STORAGE_CURRENT_LABORATORY, STORAGE_RECOVER_PASSWORD } from 'constants/'
import { InitialTrackedSteps, InitialTrackedPageView } from './analytics'

// Components
import { Header, TemplateContainer } from 'components'
import { NewPasswordWrapper, ValidateTokenWrapper, SelectContactWrapper } from './Wrappers'
import { CurrentLaboratoryType } from 'typings/auth'

const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''

const HeaderByStep: Record<RecoverStep, HeaderProps> = {
    [RecoverStep.selectContact]: {
        title: 'Preciso validar sua conta, ok?',
        subtitle: 'Pra qual contato abaixo posso enviar o código de validação da sua conta Nav Dasa?',
    },
    [RecoverStep.validateToken]: {
        title: 'Valide sua conta',
        subtitle: 'Informe o código de 6 dígitos que foi enviado para o [CONTACT_TYPE] <span>[CONTACT_VALUE]</span>.',
    },
    [RecoverStep.newPassword]: {
        title: 'Crie uma nova senha',
        subtitle: 'Considere as regras abaixo para criar uma senha segura:',
    },
}

interface LocationState {
    redirectTo?: string
    federalId?: string
}

export const RecoverPassword: React.VFC = () => {
    const sessionStorageState: LocationState = useMemo(() => JSON.parse(String(sessionStorage.getItem(STORAGE_RECOVER_PASSWORD))), [])
    const { step, ...recover } = useRecoverHook()
    const history = useHistory()
    const location = useLocation<LocationState>()

    const currentLaboratory: CurrentLaboratoryType = JSON.parse(sessionStorage.getItem(STORAGE_CURRENT_LABORATORY) || '{}')
    const logoAuxiliar = currentLaboratory.layout !== 'singleBrand' ? currentLaboratory : undefined

    const SUBTITLE_CONTENT: string = useMemo(() => {
        let {
            [step]: { subtitle },
        } = HeaderByStep
        if (step === RecoverStep.validateToken) {
            subtitle = subtitle.replace('[CONTACT_VALUE]', recover.contact?.value || '')
            subtitle = subtitle.replace('[CONTACT_TYPE]', recover.contact?.type === 'EMAIL' ? 'e-mail' : 'celular')
        }

        return subtitle
    }, [recover.contact, step])

    usePageTracker<RecoverStep>(InitialTrackedSteps, step)
    usePageTracker<RecoverStep>(InitialTrackedPageView, step)

    const checkCurrentStep = useCallback(() => {
        const stepQueryString = (getQueryParamsValue(location, 'step') as unknown) as RecoverStep

        if (!stepQueryString) {
            history.replace(`${location.pathname}?step=0`, location.state)
            recover.setCurrentStep(0)
        } else {
            recover.setCurrentStep(+stepQueryString)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, location])

    useEffect(() => {
        checkCurrentStep()
    }, [checkCurrentStep])

    useEffect(() => {
        if (sessionStorageState?.redirectTo && !recover.redirectTo) {
            recover.setRedirectTo(sessionStorageState.redirectTo)
        }
    }, [recover, location, sessionStorageState])

    if (!recover.hasMounted) {
        return null
    }

    return (
        <TemplateContainer>
            <PageTemplate>
                <Header logoAux={logoAuxiliar} subtitle={SUBTITLE_CONTENT} title={HeaderByStep[step].title} />
                {(() => {
                    switch (step) {
                        case RecoverStep.selectContact:
                            return <SelectContactWrapper />
                        case RecoverStep.validateToken:
                            return <ValidateTokenWrapper />
                        default:
                            return (
                                <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey} useEnterprise>
                                    <NewPasswordWrapper />
                                </GoogleReCaptchaProvider>
                            )
                    }
                })()}
            </PageTemplate>
        </TemplateContainer>
    )
}
